/** Conversation behaviour: sending, rendering, guided flows, end-of-chat survey. */

import { ChatApiError, streamMessage } from "./api";
import { CHATBOT_NAME, MAX_MESSAGE_LENGTH, RATE_LIMIT, type WidgetConfig } from "./config";
import { botIconImg, type WidgetElements } from "./dom";
import { renderMarkdown } from "./markdown";
import type { ReCaptcha } from "./recaptcha";
import { randomThinkingWord, STAGE_WORDS, type Stage } from "./thinking-words";

const CONVERSATION_KEY = "esl-chat-conversation-id";
const ROTATE_MS = 3000;

// A guided flow asks for one detail, then wraps the user's reply in a full question.
const FLOW_PREFIX = {
  track: "What is the current status of my shipment ",
  eta: "What is the ETA and ETD of my shipment ",
  route: "What is the sailing schedule and transit time from ",
  vessel: "What is the current schedule for vessel ",
} as const;
type Flow = keyof typeof FLOW_PREFIX;

const SURVEY_FACES = {
  negative: ["Not satisfied", "#d32f2f", "#b71c1c", '<path d="M 12 26 Q 20 22 28 26" stroke="#000" stroke-width="2" fill="none" stroke-linecap="round"/>'],
  neutral: ["Neutral", "#ffa726", "#f57c00", '<line x1="12" y1="26" x2="28" y2="26" stroke="#000" stroke-width="2" stroke-linecap="round"/>'],
  positive: ["Satisfied", "#388e3c", "#2e7d32", '<path d="M 12 24 Q 20 30 28 24" stroke="#000" stroke-width="2" fill="none" stroke-linecap="round"/>'],
} as const;

function currentTime(): string {
  return new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function timestamp(): HTMLDivElement {
  const div = document.createElement("div");
  div.className = "message-timestamp";
  div.textContent = currentTime();
  return div;
}

/** Strip markup and enforce the length cap. Null means "don't send". */
export function sanitizeInput(input: string): string | null {
  const cleaned = input
    .trim()
    .replace(/<script[^>]*>.*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, "");
  return cleaned && cleaned.length <= MAX_MESSAGE_LENGTH ? cleaned : null;
}

class RateLimiter {
  private requests: number[] = [];

  tryAcquire(): number {
    const now = Date.now();
    this.requests = this.requests.filter((t) => now - t < RATE_LIMIT.windowMs);
    if (this.requests.length >= RATE_LIMIT.maxRequests) {
      return Math.ceil((RATE_LIMIT.windowMs - (now - this.requests[0])) / 1000);
    }
    this.requests.push(now);
    return 0;
  }
}

export class Chat {
  private busy = false;
  private flow: Flow | null = null;
  private conversationId: string;
  private readonly limiter = new RateLimiter();

  constructor(
    private readonly ui: WidgetElements,
    private readonly config: WidgetConfig,
    private readonly recaptcha: ReCaptcha | null,
  ) {
    this.conversationId = localStorage.getItem(CONVERSATION_KEY) ?? this.rotateConversation();
  }

  // -- sending -----------------------------------------------------------------------

  async send(raw: string): Promise<void> {
    if (this.busy) return;
    if (raw.trim().length > MAX_MESSAGE_LENGTH) {
      this.notice(`Message too long (max ${MAX_MESSAGE_LENGTH} characters)`, "error");
      return;
    }
    const text = sanitizeInput(raw);
    if (!text) return;

    const wait = this.limiter.tryAcquire();
    if (wait) {
      this.notice(`Please wait ${wait} seconds before sending another message.`, "warning");
      return;
    }

    const request = this.flow ? FLOW_PREFIX[this.flow] + text : text;
    this.flow = null;
    this.hideWelcome();
    this.addUserMessage(text);
    this.ui.input.value = "";
    this.setBusy(true);
    const progress = this.showProgress();

    try {
      const challenge = this.recaptcha ? await this.recaptcha.token() : null;
      const reply = await streamMessage(
        this.config.apiUrl,
        {
          text: request,
          conversation_id: this.conversationId,
          market: this.config.market,
          challenge_token: challenge,
        },
        (stage) => progress.setStage(stage),
      );
      progress.stop();
      this.renderBotMessage(reply.body, reply.outcome === "refused");
    } catch (error) {
      progress.stop();
      const message =
        error instanceof ChatApiError || error instanceof Error
          ? error.message
          : "Sorry, a connection error occurred.";
      this.renderBotMessage(message);
    } finally {
      this.setBusy(false);
    }
  }

  // -- welcome actions and guided flows ---------------------------------------------

  handleAction(action: string): void {
    this.hideWelcome();
    this.clearTransient();
    switch (action) {
      case "track":
        this.ask("Please share your BL, booking or container number.", "track");
        break;
      case "eta":
        this.ask("Please share your BL, booking or container number.", "eta");
        break;
      case "route":
        this.ask("Which ports are you sailing between? For example, Jebel Ali to Nhava Sheva.", "route");
        break;
      case "vessel":
        this.ask("Which vessel or voyage? For example, ESL Dubai or voyage 241W.", "vessel");
        break;
    }
  }

  private ask(question: string, flow: Flow): void {
    this.renderBotMessage(question);
    this.flow = flow;
    this.setBusy(false);
  }

  // -- ending a conversation ----------------------------------------------------------

  /** The in-chat "End chat" button: close the conversation and ask for a rating. */
  endChat(): void {
    this.ui.sessionBar.hidden = true;
    this.clearTransient();
    this.showSurvey();
  }

  private showSurvey(): void {
    const survey = document.createElement("div");
    survey.className = "chat-satisfaction-survey";
    const faces = Object.entries(SURVEY_FACES)
      .map(
        ([rating, [title, fill, stroke, mouth]]) =>
          `<span class="chat-survey-emoji" data-rating="${rating}" title="${title}">
             <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
               <circle cx="20" cy="20" r="18" fill="${fill}" stroke="${stroke}" stroke-width="2"/>
               <circle cx="14" cy="16" r="2" fill="#000"/><circle cx="26" cy="16" r="2" fill="#000"/>${mouth}
             </svg>
           </span>`,
      )
      .join("");
    survey.innerHTML = `
      <div class="chat-survey-title">How satisfied were you with ${CHATBOT_NAME} today?</div>
      <div class="chat-survey-emojis">${faces}</div>
      <div class="chat-survey-end-message">Your chat has ended.<br>
        <a href="#" class="chat-new-session-link">Click here for a new chat</a></div>`;

    for (const face of survey.querySelectorAll<HTMLElement>(".chat-survey-emoji")) {
      face.addEventListener("click", () => {
        for (const other of survey.querySelectorAll<HTMLElement>(".chat-survey-emoji")) {
          other.classList.add(other === face ? "selected" : "disabled");
        }
        // ponytail: the rating is shown, not stored — esl-chat has no feedback endpoint
        // yet (the `feedback` table exists). POST it here once one is added.
        window.setTimeout(() => {
          survey.innerHTML = `<div class="chat-survey-thank-you">Thank you for your feedback!<br><br>
            Your chat has ended.<br><a href="#" class="chat-new-session-link">Click here for a new chat</a></div>`;
          this.bindNewChatLink(survey);
        }, 800);
      });
    }
    this.bindNewChatLink(survey);
    this.insert(survey);
    this.ui.input.disabled = true;
    this.ui.input.placeholder = "Chat has ended";
    this.ui.sendBtn.disabled = true;
  }

  private bindNewChatLink(container: HTMLElement): void {
    container.querySelector(".chat-new-session-link")?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.newConversation();
    });
  }

  /** The header "+" button and the survey's new-chat link both land here. */
  newConversation(): void {
    this.ui.sessionBar.hidden = true;
    for (const child of [...this.ui.messages.children]) {
      if (child !== this.ui.welcomeScreen && child !== this.ui.thinkingIndicator) child.remove();
    }
    this.ui.welcomeScreen.classList.remove("hidden");
    this.flow = null;
    this.rotateConversation();
    this.ui.input.placeholder = "Ask about a shipment…";
    this.setBusy(false);
    this.scrollToBottom(true);
  }

  // -- rendering ----------------------------------------------------------------------

  private addUserMessage(text: string): void {
    const message = document.createElement("div");
    message.className = "chat-message user-message";
    const content = document.createElement("div");
    content.textContent = text;
    message.append(content, timestamp());
    this.insert(message);
  }

  /** `notice` marks a refusal (e.g. E-Sea sign-in required) so it reads as a callout. */
  renderBotMessage(markdown: string, notice = false): void {
    const wrapper = this.botWrapper("");
    if (notice) wrapper.querySelector(".bot-message")?.classList.add("bot-message-notice");
    const content = wrapper.querySelector<HTMLElement>(".bot-message-content");
    if (content) renderMarkdown(content, markdown);
    // A reply that opens with a quote opens with a warning (sensitive shipment data):
    // the whole bubble turns yellow, like a refusal notice.
    if (content?.firstElementChild?.tagName === "BLOCKQUOTE") {
      wrapper.querySelector(".bot-message")?.classList.add("bot-message-notice");
    }
  }

  /** Avatar + "ESL Bot" label + bubble, as the original renderBotMessage built it. */
  private botWrapper(contentHtml: string): HTMLDivElement {
    const wrapper = document.createElement("div");
    wrapper.className = "bot-message-wrapper";
    const avatar = document.createElement("div");
    avatar.className = "bot-avatar-section";
    avatar.append(botIconImg("bot-message-icon"));

    const section = document.createElement("div");
    section.className = "bot-message-section";
    const message = document.createElement("div");
    message.className = "chat-message bot-message";
    const content = document.createElement("div");
    content.className = "bot-message-content";
    content.innerHTML = contentHtml;
    message.append(content, timestamp());
    section.append(message);
    wrapper.append(avatar, section);
    this.insert(wrapper);
    return wrapper;
  }

  /** "✻ Charting… 3s". The word rotates every 3 s; each progress event from the backend
   * switches to that stage's words and swaps the word at once. */
  private showProgress(): { setStage: (stage: Stage) => void; stop: () => void } {
    const progress = document.createElement("div");
    progress.className = "progress-message";
    progress.setAttribute("role", "status");
    progress.innerHTML = `<span class="progress-glyph" aria-hidden="true"></span>
      <span class="progress-text"></span><span class="progress-elapsed" aria-hidden="true"></span>`;
    const text = progress.querySelector<HTMLElement>(".progress-text")!;
    const elapsed = progress.querySelector<HTMLElement>(".progress-elapsed")!;
    let pool: readonly string[] | undefined;
    let word = randomThinkingWord();
    const show = () => {
      word = randomThinkingWord(pool, word);
      text.textContent = `${word}…`;
    };
    text.textContent = `${word}…`;

    const started = Date.now();
    const clock = window.setInterval(() => {
      elapsed.textContent = `${Math.round((Date.now() - started) / 1000)}s`;
    }, 1000);
    let rotation = window.setInterval(show, ROTATE_MS);
    this.insert(progress);

    return {
      setStage: (stage) => {
        pool = STAGE_WORDS[stage];
        show();
        // Restart the 3 s rotation so a fresh word never flips away immediately.
        window.clearInterval(rotation);
        rotation = window.setInterval(show, ROTATE_MS);
      },
      stop: () => {
        window.clearInterval(clock);
        window.clearInterval(rotation);
        progress.remove();
      },
    };
  }

  notice(text: string, type: "info" | "warning" | "error"): void {
    const message = document.createElement("div");
    message.className = `chat-security-message chat-${type}`;
    message.textContent = text;
    this.insert(message, false);
    window.setTimeout(() => message.remove(), 5000);
  }

  private insert(node: HTMLElement, force = true): void {
    this.ui.thinkingIndicator.before(node);
    this.scrollToBottom(force);
  }

  scrollToBottom(force = false): void {
    const m = this.ui.messages;
    if (force || m.scrollHeight - m.clientHeight <= m.scrollTop + 50) m.scrollTop = m.scrollHeight;
  }

  private hideWelcome(): void {
    this.ui.welcomeScreen.classList.add("hidden");
    this.ui.sessionBar.hidden = false;
  }

  private clearTransient(): void {
    this.ui.messages
      .querySelectorAll(".chat-satisfaction-survey")
      .forEach((n) => n.remove());
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.ui.input.disabled = busy;
    this.ui.sendBtn.disabled = busy;
    if (!busy) requestAnimationFrame(() => this.ui.input.focus());
  }

  private rotateConversation(): string {
    this.conversationId = crypto.randomUUID();
    localStorage.setItem(CONVERSATION_KEY, this.conversationId);
    return this.conversationId;
  }
}
